import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  Cell, ComposedChart
} from 'recharts';
import {
  Users, Clock, Map, Target, TrendingUp, TrendingDown,
  ArrowUpRight, ArrowDownRight, Activity, Calendar, 
  Share2, Eye, Home, Building, MessageCircle, UserCheck,
  Shield, Globe, Smartphone, Desktop, BarChart3, PieChart as PieChartIcon
} from 'lucide-react';

const COLORS = {
  primary: '#3B82F6',
  secondary: '#6366F1', 
  tertiary: '#8B5CF6',
  accent: '#EC4899',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  gray: '#6B7280'
};

const CHART_COLORS = [COLORS.primary, COLORS.secondary, COLORS.tertiary, COLORS.accent];

const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue', className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 ${className}`}>
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-3 rounded-xl bg-${color}-50`}>
            <Icon className={`w-6 h-6 text-${color}-600`} />
          </div>
          {trend !== undefined && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium
              ${trend > 0 ? 'bg-green-50 text-green-700' : trend < 0 ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}>
              {trend > 0 ? <ArrowUpRight className="w-4 h-4" /> : 
               trend < 0 ? <ArrowDownRight className="w-4 h-4" /> : null}
              {Math.abs(trend)}%
            </div>
          )}
        </div>
        <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
        <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
    </div>
  </div>
);

const ChartSection = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-gray-100 ${className}`}>
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 rounded-lg bg-blue-50">
        <Icon className="w-5 h-5 text-blue-600" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
    </div>
    {children}
  </div>
);

const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
    <div className="relative">
      <div className="w-20 h-20 border-4 border-blue-100 rounded-full animate-spin">
        <div className="absolute top-0 left-0 w-20 h-20 border-4 border-blue-500 rounded-full animate-pulse border-t-transparent"></div>
      </div>
      <div className="mt-4 text-center">
        <p className="text-gray-600 font-medium">Cargando estadísticas...</p>
      </div>
    </div>
  </div>
);

const ErrorDisplay = ({ error, onRetry }) => (
  <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-gray-100">
      <div className="bg-red-50 w-16 h-16 rounded-full mx-auto mb-6 p-4">
        <Activity className="w-full h-full text-red-500" />
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Error al cargar datos</h2>
      <p className="text-gray-600 mb-6">{error}</p>
      <button
        onClick={onRetry}
        className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 
                  transition-all duration-300 transform hover:scale-105 active:scale-95 font-medium"
      >
        Reintentar
      </button>
    </div>
  </div>
);

const PropertyStatistics = () => {
  const { propertyId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [propertyDetails, setPropertyDetails] = useState(null);
  const [userTypeAnalysis, setUserTypeAnalysis] = useState(null);
  const [listingType, setListingType] = useState(() => {
    const queryParams = new URLSearchParams(location.search);
    const typeFromQuery = queryParams.get('type');
    
    if (typeFromQuery === 'rental' || typeFromQuery === 'property') {
      return typeFromQuery;
    }
    
    if (location.state?.listingType) {
      return location.state.listingType === 'rent' ? 'rental' : 'property';
    }
    
    return null;
  });

  const queryType = (() => {
    const queryParams = new URLSearchParams(location.search);
    const typeFromQuery = queryParams.get('type');
    
    if (typeFromQuery === 'rental' || typeFromQuery === 'property') {
      return typeFromQuery;
    }
    
    return listingType;
  })();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No hay token disponible');

        const fetchWithErrorHandling = async (url) => {
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
          }

          return response.json();
        };

        const currentType = queryType || 'property';
        let propertyData;
        let endpointType;
        
        // Obtener datos de la propiedad
        if (currentType === 'rental') {
          try {
            propertyData = await fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/alquileres/${propertyId}`);
            endpointType = 'rental';
          } catch (error) {
            propertyData = await fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/propiedades/${propertyId}`);
            endpointType = 'property';
          }
        } else {
          try {
            propertyData = await fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/propiedades/${propertyId}`);
            endpointType = 'property';
          } catch (error) {
            propertyData = await fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/alquileres/${propertyId}`);
            endpointType = 'rental';
          }
        }
        
        setListingType(endpointType);
        
        // Obtener todas las estadísticas en paralelo
        const [statsData, averageVisitTimeData, userAnalysisData] = await Promise.all([
          fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/statistics/property/${propertyId}?type=${endpointType}`),
          fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/statistics/average-visit-time?propertyId=${propertyId}&type=${endpointType}`),
          fetchWithErrorHandling(`${process.env.REACT_APP_API_URL || 'https://backend-production-1786.up.railway.app'}/api/statistics/user-type-analysis/${propertyId}?type=${endpointType}`)
        ]);

        setStats({
          ...statsData,
          averageVisitTime: averageVisitTimeData.formattedTime || '0s'
        });
        
        setPropertyDetails(propertyData);
        setUserTypeAnalysis(userAnalysisData);
        
      } catch (error) {
        setError(error.message || 'Error desconocido al cargar los datos');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [propertyId, queryType]);

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorDisplay error={error} onRetry={() => window.location.reload()} />;

  const isRental = listingType === 'rental' || 
                  (propertyDetails && propertyDetails.hasOwnProperty('precio_mensual')) ||
                  (propertyDetails && propertyDetails.operacion === 'alquiler');

  const PropertyIcon = isRental ? Building : Home;

  const getPriceText = () => {
    if (isRental) {
      return `S/ ${new Intl.NumberFormat('es-PE').format(propertyDetails?.precio_mensual || 0)}/mes`;
    } else {
      return `S/ ${new Intl.NumberFormat('es-PE').format(propertyDetails?.precio_soles || 0)}`;
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('es-PE').format(num || 0);
  };

  const getConversionRate = () => {
    const totalVisits = stats?.totalVisits || 0;
    const contacts = stats?.contactClicks || 0;
    return totalVisits > 0 ? ((contacts / totalVisits) * 100).toFixed(1) : 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg">
                  <PropertyIcon className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">
                    {isRental ? 'Propiedad en Alquiler' : 'Propiedad en Venta'}
                  </span>
                </div>
                <div className="px-3 py-1.5 bg-gray-100 rounded-lg">
                  <span className="text-sm font-mono text-gray-600">ID: {propertyId}</span>
                </div>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                {propertyDetails?.titulo || 'Estadísticas de la Propiedad'}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-600">
                <div className="flex items-center gap-2">
                  <Map className="w-4 h-4" />
                  <span>{propertyDetails?.ubicacion || 'Ubicación no especificada'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  <span className="font-semibold text-blue-600">{getPriceText()}</span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3">
              <div className="text-right">
                <p className="text-sm text-gray-500">Tasa de Conversión</p>
                <p className="text-2xl font-bold text-green-600">{getConversionRate()}%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Métricas Principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Visitas Totales"
            value={formatNumber(stats?.totalVisits)}
            subtitle={`${formatNumber(stats?.uniqueVisitors)} visitantes únicos`}
            icon={Eye}
            trend={12}
            color="blue"
          />
          <StatCard
            title="Tiempo Promedio"
            value={stats?.averageVisitTime || "0s"}
            subtitle={`${formatNumber(stats?.totalTime)} seg. total`}
            icon={Clock}
            trend={-5}
            color="purple"
          />
          <StatCard
            title="Clicks de Contacto"
            value={formatNumber(stats?.contactClicks)}
            subtitle={`${getConversionRate()}% tasa de conversión`}
            icon={Share2}
            trend={8}
            color="pink"
          />
          <StatCard
            title="Mensajes de Contacto"
            value={formatNumber(stats?.messageContactClicks)}
            subtitle={`${stats?.messageContactClicks > 0 ? ((stats.messageContactClicks / (stats.totalVisits || 1)) * 100).toFixed(1) : 0}% de visitas`}
            icon={MessageCircle}
            trend={15}
            color="indigo"
          />
        </div>

        {/* Análisis de Usuarios */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartSection title="Tipos de Visitantes" icon={Users}>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-blue-50 rounded-xl">
                <Shield className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-blue-600">{formatNumber(userTypeAnalysis?.authenticated?.visits || 0)}</p>
                <p className="text-sm text-gray-600">Usuarios Registrados</p>
                <p className="text-xs text-gray-500 mt-1">{userTypeAnalysis?.authenticated?.conversionRate || 0}% conversión</p>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-xl">
                <Globe className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-gray-600">{formatNumber(userTypeAnalysis?.anonymous?.visits || 0)}</p>
                <p className="text-sm text-gray-600">Usuarios Anónimos</p>
                <p className="text-xs text-gray-500 mt-1">{userTypeAnalysis?.anonymous?.conversionRate || 0}% conversión</p>
              </div>
            </div>
            
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={[
                    { name: 'Registrados', value: userTypeAnalysis?.authenticated?.visits || 0, color: COLORS.primary },
                    { name: 'Anónimos', value: userTypeAnalysis?.anonymous?.visits || 0, color: COLORS.gray }
                  ]}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {[0, 1].map((index) => (
                    <Cell key={`cell-${index}`} fill={index === 0 ? COLORS.primary : COLORS.gray} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatNumber(value), 'Visitas']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </ChartSection>

          <ChartSection title="Engagement por Semana" icon={TrendingUp}>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={stats?.weeklyEngagement || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="week" 
                  stroke="#666"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#666" tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [formatNumber(value), name]}
                />
                <Bar dataKey="visits" fill={COLORS.primary} name="Visitas" radius={[4, 4, 0, 0]} />
                <Line 
                  type="monotone" 
                  dataKey="contacts" 
                  stroke={COLORS.accent} 
                  strokeWidth={3}
                  name="Contactos"
                  dot={{ fill: COLORS.accent, strokeWidth: 2, r: 4 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </ChartSection>
        </div>

        {/* Gráficos de Análisis */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartSection title="Tendencia de Visitas (30 días)" icon={Calendar}>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={stats?.visitTrends?.daily || []}>
                <defs>
                  <linearGradient id="colorVisits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="date" 
                  stroke="#666"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => new Date(value).toLocaleDateString('es-PE', { month: 'short', day: 'numeric' })}
                />
                <YAxis stroke="#666" tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  labelFormatter={(value) => new Date(value).toLocaleDateString('es-PE')}
                  formatter={(value, name) => [formatNumber(value), name]}
                />
                <Area 
                  type="monotone" 
                  dataKey="visits" 
                  stroke={COLORS.primary}
                  strokeWidth={2}
                  fill="url(#colorVisits)"
                  name="Visitas"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartSection>

          <ChartSection title="Horarios de Mayor Actividad" icon={Clock}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats?.visitTimes || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="hour" 
                  stroke="#666"
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#666" tick={{ fontSize: 12 }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => [formatNumber(value), 'Visitas']}
                />
                <Bar 
                  dataKey="visits" 
                  fill={COLORS.secondary}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartSection>
        </div>

        {/* Distribuciones */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <ChartSection title="Distribución por Edad" icon={PieChartIcon}>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats?.demographics?.ageGroups || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="count"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                >
                  {(stats?.demographics?.ageGroups || []).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [formatNumber(value), 'Visitantes']} />
              </PieChart>
            </ResponsiveContainer>
          </ChartSection>

          <ChartSection title="Distribución Geográfica" icon={Map}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                layout="vertical" 
                data={stats?.geographicDistribution?.slice(0, 8) || []}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#666" tick={{ fontSize: 12 }} />
                <YAxis 
                  dataKey="ubicacion" 
                  type="category" 
                  width={100} 
                  stroke="#666"
                  tick={{ fontSize: 12 }}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value) => [formatNumber(value), 'Visitas']}
                />
                <Bar 
                  dataKey="visits" 
                  fill={COLORS.tertiary}
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartSection>
        </div>

        {/* Tabla de Métricas Detalladas */}
        <ChartSection title="Métricas Detalladas de Rendimiento" icon={BarChart3} className="mb-8">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-900">Métrica</th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-900">Valor</th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-900">Tipo de Usuario</th>
                  <th className="py-4 px-4 text-left text-sm font-semibold text-gray-900">Tendencia</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-4 text-sm text-gray-900">Visitas totales</td>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{formatNumber(stats?.totalVisits)}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">Todos los usuarios</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">+12%</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-4 text-sm text-gray-900">Tiempo promedio por visita</td>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{stats?.averageVisitTime}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">Todos los usuarios</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-red-600">
                      <TrendingDown className="w-4 h-4" />
                      <span className="text-sm font-medium">-5%</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-4 text-sm text-gray-900">Usuarios registrados</td>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{formatNumber(userTypeAnalysis?.authenticated?.visits)}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">Autenticados</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">+18%</span>
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-gray-100">
                  <td className="py-4 px-4 text-sm text-gray-900">Usuarios anónimos</td>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{formatNumber(userTypeAnalysis?.anonymous?.visits)}</td>
                  <td className="py-4 px-4 text-sm text-gray-600">Anónimos</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">+8%</span>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="py-4 px-4 text-sm text-gray-900">Tasa de conversión global</td>
                  <td className="py-4 px-4 text-sm font-medium text-gray-900">{getConversionRate()}%</td>
                  <td className="py-4 px-4 text-sm text-gray-600">Todos los usuarios</td>
                  <td className="py-4 px-4">
                    <div className="flex items-center gap-1 text-green-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-medium">+15%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </ChartSection>

        {/* Footer Info */}
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 text-center border border-blue-100">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Activity className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-blue-900">Estadísticas en Tiempo Real</span>
          </div>
          <p className="text-blue-700 mb-1">
            <span className="font-medium">ID de {isRental ? 'Alquiler' : 'Propiedad'}:</span> {propertyId}
          </p>
          <p className="text-sm text-blue-600">
            Datos actualizados automáticamente • Incluye usuarios anónimos y registrados
          </p>
          <div className="flex items-center justify-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
              <span className="text-blue-700">Usuarios Registrados</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
              <span className="text-blue-700">Usuarios Anónimos</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyStatistics;